<template>
  <div id="map" class="dashboard-map" type="light" oncontextmenu="return false">
    <center-workspace-button
      :disabled="!workspaceFeature"
      :loading="workspaceIsLoading"
      @click="centerOnWorkspace"
      :viewIsAerial="isAerial"
    />
    <change-map-type-button @click="changeViewType" :viewIsAerial="isAerial" />
  </div>
</template>

<script>
import { managementMapsMixin } from "@/mixins/management/managementMapsMixin";
import { GeoJSON } from "ol/format";
import { mapActions } from "vuex";
import utils from "@/utils";
import CenterWorkspaceButton from "@/components/_Atoms/CenterLocationButton";
import ChangeMapTypeButton from "@/components/_Atoms/ChangeMapTypeButton";
import { Fill, Stroke, Style } from "ol/style.js";

export default {
  name: "ManagementSideMapShowWorkspace",

  components: {
    CenterWorkspaceButton,
    ChangeMapTypeButton
  },

  mixins: [managementMapsMixin],

  props: {
    /**
     * Receive the uuid of the selected workspace
     */
    workspaceUuid: String,

    /**
     * Receive the reservations to create a feature for each reservation
     * with its geom to add to the map
     */
    reservations: Array,

    /**
     * Determines if the sidebar is open or not
     */
    sidebarIsOpen: Boolean,

    /**
     * Get the geom of an activity to create a feature to add to the map
     */
    activityGeom: Array
  },

  data() {
    return {
      /**
       * Store the selected workspace
       */
      workspace: null,

      /**
       * Determines if you are still waiting for BE's response
       */
      workspaceIsLoading: false,

      /**
       * Workspace feature used in the map
       */
      workspaceFeature: undefined
    };
  },

  computed: {
    /**
     * Returns the style of the reservation area that will be shown on the map
     * @returns {Style}
     */
    reservationAreaStyle() {
      let defaultStroke = new Stroke({
        color: "accent",
        width: 1.25
      });

      let defaultFill = new Fill({
        color: "rgba(79, 162, 183, 0.5)"
      });

      return new Style({
        fill: defaultFill,
        stroke: defaultStroke
      });
    }
  },

  watch: {
    /**
     * Whenever the workspace uuid changes, the updateWorkspace method is called.
     */
    workspaceUuid() {
      this.updateWorkspace();
    },

    /**
     * Whenever the activityGeom changes, the updateActivityFeatures method is called.
     */
    activityGeom() {
      this.updateActivityFeatures();
    },

    /**
     * Whenever the value of the sidebarIsOpen is changed,
     * the map size is updated and the updateWorkspace method is called.
     */
    sidebarIsOpen() {
      this.map.updateSize();
      this.updateWorkspace();
    }
  },

  /**
   * When starting the component it is necessary to create the default styles that will be used in the map,
   * create the map using Openlayers and the aerial map layer.
   */
  mounted() {
    this.createDefaultStyle();
    this.createMap();
    this.createAerialMapLayer();
    this.vectorLayer.setZIndex(4);
    if (this.workspaceUuid) {
      this.updateWorkspace();
    }
  },

  methods: {
    ...mapActions(["fetchWorkspaceByUUID"]),

    /**
     * Get the workspace from the uuid and emit an event to the
     * parent component sending the geom of the new workspace
     * @returns {Promise<void>}
     */
    async updateWorkspace() {
      try {
        this.workspaceFeature = undefined;
        this.vectorSource.clear();
        this.map.updateSize();
        if (this.workspaceUuid) {
          this.workspaceIsLoading = true;
          let workspace = await this.fetchWorkspaceByUUID({
            uuid: this.workspaceUuid,
            includes: null,
            fields: "wp_geo_json,geom,wp_id"
          });
          this.showWorkspace(workspace.wp_geo_json);
          this.$emit("workspace-geom", workspace.geom);
          this.updateReservationsFeatures();
        }
      } catch (e) {
        let errorHandle = utils.errorHandler(e);
        if (errorHandle && typeof errorHandle === "string") {
          this.$toasted.global.toast_error(errorHandle);
        }
      } finally {
        this.workspaceIsLoading = false;
      }
    },

    /**
     * Update the workspace feature to show on the map
     * @param geoJson
     */
    showWorkspace(geoJson) {
      this.workspaceFeature = this.getFeature(geoJson);
      this.vectorSource.addFeature(this.workspaceFeature);
      this.centerOnWorkspace();
    },

    /**
     * Create a new feature from the received geom
     * @param geoJSON
     * @returns {*} -> the feature created
     */
    getFeature(geoJSON) {
      let feature = new GeoJSON().readFeatures(geoJSON, {
        featureProjection: "EPSG:3857",
        dataProjection: "EPSG:4326"
      });
      return feature[0];
    },

    /**
     * Centers the map at the center of the selected workspace feature
     */
    centerOnWorkspace() {
      let workspaceExtent = this.workspaceFeature.getGeometry();
      this.map.getView().fit(workspaceExtent, this.map.getSize());
    },

    /**
     * for each reservations a feature is created for each one with the geom and
     * adds the vectorSource variable to be displayed on the map
     */
    updateReservationsFeatures() {
      if (this.reservations.length > 0) {
        for (const reservation of this.reservations) {
          let feature = this.getFeature(reservation.ar_geo_json);
          feature.setStyle(this.reservationAreaStyle);
          this.vectorSource.addFeature(feature);
        }
      }
    },

    /**
     * For each activity geom a feature is created for each one with the geom and
     * add the vectorSource variable to be displayed on the map
     */
    updateActivityFeatures() {
      for (const activity of this.activityGeom) {
        let feature = this.getFeature(activity);
        this.vectorSource.addFeature(feature);
        feature.setStyle();
      }
    }
  }
};
</script>

<style scoped>
.dashboard-map {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/Management/ManagementSideMapWorkspace.vue